import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const FAQS = [
  {
    q: 'Do I need to make an appointment?',
    a: 'It\'s super easy — just give us a call or send a message any time Mon–Sat, 9am–9pm. We\'ll confirm your instrument and coordinate a time to come to you. No waiting lists, no commitment of any kind.',
  },
  {
    q: 'How much does a walk-in lesson cost?',
    a: 'Just $30 for a full hour. You pay at the door with cash or card. No hidden fees, registration charges, or anything else — $30 is the full price.',
  },
  {
    q: 'What instruments can I try?',
    a: 'We currently offer walk-in sessions for Piano, Vocals, and Drums. Each discipline has a dedicated instructor ready to work with you at your exact level.',
  },
  {
    q: 'What skill level do I need to be?',
    a: 'Zero. Walk-in sessions are available for absolute beginners all the way through advanced students. Your instructor will assess your level in the first few minutes and tailor everything from there.',
  },
  {
    q: 'How long is a walk-in session?',
    a: 'Each session is one full hour of dedicated instruction. If you want more time, you\'re welcome to book back-to-back sessions (based on availability) or ask about our enrollment options for recurring lessons.',
  },
  {
    q: 'Can I book an evening session?',
    a: 'Absolutely — evening sessions are available every day we\'re open. Just reach out before 8:00 PM so we can get your full hour in before 9pm. Evening rates are identical: $30 flat, no surcharge.',
  },
  {
    q: 'What do I need to have ready?',
    a: 'Just yourself and $30. That\'s genuinely all. We bring the instruments and equipment to you. If you have sheet music you\'d like to work on, have it handy — but it\'s completely optional.',
  },
  {
    q: 'Can I become a regular student after a session?',
    a: 'Yes, and many students do! After your first session you can talk to our team about enrollment options, recurring lesson packages, or in-home programs. There\'s zero pressure — it\'s just there if you want it.',
  },
];

export default function WalkInFAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });

  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[450px] h-[450px] bg-[#EEF2FF]/40 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-16 items-start">

          {/* Sticky left label */}
          <div className={`scroll-hidden slide-left ${isVisible ? 'scroll-visible' : ''} lg:sticky lg:top-32`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#EEF2FF] text-brand-blue-dark font-body text-xs font-bold uppercase tracking-widest border border-brand-blue/10 mb-5">
              Common Questions
            </span>
            <h2 className="font-heading text-brand-blue-dark text-4xl font-black leading-tight mb-4">
              Walk-In FAQ
            </h2>
            <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
              Everything you need to know before showing up. (Spoiler: it really is as easy as it sounds.)
            </p>
            <div className="flex flex-col gap-4 p-6 rounded-2xl bg-[#FFFBEB] border border-brand-yellow/25">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                  <i className="ri-phone-line text-base" />
                </div>
                <div>
                  <p className="font-body text-brand-blue-dark font-bold text-sm">Still have questions?</p>
                  <a href="tel:9102424567" className="font-body text-brand-yellow-dark text-sm font-semibold hover:underline cursor-pointer">
                    910-242-4567
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Accordion */}
          <div className={`scroll-hidden slide-right ${isVisible ? 'scroll-visible' : ''} flex flex-col gap-3`}>
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                  open === i
                    ? 'border-brand-yellow/40 bg-[#FFFBEB]/60'
                    : 'border-gray-100 bg-white hover:border-brand-yellow/30'
                }`}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="whitespace-nowrap w-full flex items-center justify-between px-6 py-5 cursor-pointer text-left gap-4"
                >
                  <span className="font-body font-semibold text-brand-blue-dark text-sm leading-snug">
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                    open === i
                      ? 'bg-brand-yellow text-brand-blue-dark rotate-45'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <i className="ri-add-line text-sm" />
                  </div>
                </button>
                <div className={`transition-all duration-300 overflow-hidden ${open === i ? 'max-h-52' : 'max-h-0'}`}>
                  <p className="font-body text-gray-500 text-sm leading-relaxed px-6 pb-5">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
